<script lang="ts">
  import { link, location, push } from "svelte-spa-router";
  import { teamsListStore, type SelectedTeam } from "$stores/teams";
  import { orgsListStore, type Organisation } from "$stores/orgs";

  import { cn } from "$utils/ui";
  import { Button } from "$ui/button";
  import * as Command from "$ui/command";
  import * as Dialog from "$ui/dialog";
  import * as Popover from "$ui/popover";
  import TeamIcon from "$assets/icons/team.svelte";
  import ArrowUpDownIcon from "$assets/icons/arrow-up-down.svelte";
  import PlusCircle from "$assets/icons/plus-circle.svelte";
  import TickIcon from "$assets/icons/tick.svelte";

  const { selectedTeam, selectedOrg } = $props<{
    selectedTeam: SelectedTeam | undefined;
    selectedOrg: Organisation | undefined;
  }>();

  const orgs = $derived(orgsListStore);
  const teams = $derived(teamsListStore);

  let open = $state(false);
  let showTeamDialog = $state(false);

  function changeTeam(newTeam: string) {
    open = false;
    let teamPage = "";
    if ($location.startsWith("/t/")) {
      const teamPathSubComponents = $location.split("/");
      if (teamPathSubComponents.length > 3) {
        teamPage = "/" + teamPathSubComponents.slice(3).join("/");
      }
    }
    push(`/t/${newTeam}${teamPage}`);
  }

  function changeOrg(newOrg: string) {
    open = false;
    let orgPage = "";
    if ($location.startsWith("/o/")) {
      const orgPathSubComponents = $location.split("/");
      if (orgPathSubComponents.length > 3) {
        orgPage = "/" + orgPathSubComponents.slice(3).join("/");
      }
    }
    push(`/o/${newOrg}${orgPage}`);
  }
  const displaySelectOrg = $derived($location.startsWith("/o/"));
  const displaySelectTeam = $derived($location.startsWith("/t/"));
  // Only show the context switcher if there are either orgs or teams
  const showContextSwitcher = $derived(($orgs?.length || 0) + ($teams?.length || 0) > 1);
</script>

{#if showContextSwitcher}
  <div class="border-t border-b border-general mt-6 pt-2 mb-4 pb-2 relative flex flex-col">
    <Dialog.Root bind:open={showTeamDialog}>
      <Popover.Root bind:open let:ids>
        <Popover.Trigger asChild let:builder>
          <Button
            builders={[builder]}
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            aria-label="Select a team"
            class="border-0 text-black dark:text-white text-base flex justify-between items-center gap-3 font-semibold px-3 py-2 -mx-2 "
          >
            <div class="text-gray-700 [&>svg]:w-6">
              <TeamIcon />
            </div>
            <div class="flex-1 text-left text-ellipsis overflow-hidden">
              {#if displaySelectOrg}
                {selectedOrg?.alias ?? "Select an organisation"}
              {:else if displaySelectTeam}
                {selectedTeam?.alias ?? "Select a team"}
              {:else}
                "Select a team"
              {/if}
            </div>
            <div class="flex-shrink-0 hover:bg-gray-200 hover:dark:bg-primary/50 p-1 rounded-md text-gray">
              <ArrowUpDownIcon />
            </div>
          </Button>
        </Popover.Trigger>
        <Popover.Content class="w-[240px] p-0">
          <Command.Root>
            <Command.List>
              {#if $orgs.length > 0}
                <Command.Group heading="Your Organisations">
                  {#each $orgs as org}
                    <Command.Item
                      onSelect={() => changeOrg(org.id)}
                      value={org.alias}
                      class={cn("text-sm", selectedOrg?.id === org.id && "popover-item-active")}
                    >
                      <div class="flex-1 flex flex-col font-medium">
                        {org.alias}
                        <p class="text-xs font-normal">
                          {org.id}
                        </p>
                      </div>
                      {#if displaySelectOrg && selectedOrg?.id === org.id}
                        <div>
                          <TickIcon />
                        </div>
                      {/if}
                    </Command.Item>
                  {/each}
                </Command.Group>
              {/if}
              <Command.Empty>No team found.</Command.Empty>
              <Command.Group heading="Your Teams">
                {#each $teams as team}
                  <Command.Item
                    onSelect={() => changeTeam(team.id)}
                    value={team.alias}
                    class={cn("text-sm", selectedTeam?.id === team.id && "popover-item-active")}
                  >
                    <div class="flex-1 flex flex-col font-medium">
                      {team.alias}
                      <p class="text-xs font-normal">
                        {team.id}
                      </p>
                    </div>
                    {#if displaySelectTeam && selectedTeam?.id === team.id}
                      <div>
                        <TickIcon />
                      </div>
                    {/if}
                  </Command.Item>
                {/each}
              </Command.Group>
            </Command.List>
            <Command.Separator />
            <Command.List>
              <Command.Group>
                <Command.Item class="border-t border-general p-0">
                  <a
                    href="/create-team"
                    use:link
                    class="flex items-center leading-tight gap-2 text-sm font-medium [&>svg]:text-gray-700 [&>svg]:w-5 p-2 w-full"
                  >
                    <PlusCircle />
                    Create New Team
                  </a>
                </Command.Item>
              </Command.Group>
            </Command.List>
          </Command.Root>
        </Popover.Content>
      </Popover.Root>
    </Dialog.Root>
  </div>
{/if}
